import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Container,
} from "@mui/material";
import { CheckCircle, LocalShipping, LocationOn, Person } from "@mui/icons-material";

function UserSlip({ shipperData }) {
  const [rows, setRows] = useState(shipperData.status);

  return (
    <Container maxWidth="xl">
      <Box
        className="fade-in-up"
        sx={{
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          marginTop: "60px",
          marginBottom: "60px",
          border: "1px solid rgba(2, 119, 189, 0.1)",
        }}
      >
        {/* Header Banner */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #03A9F4 0%, #0277BD 100%)",
            padding: "24px 40px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <LocalShipping sx={{ fontSize: "36px", color: "white" }} />
          <Typography
            fontFamily="Montserrat"
            fontSize={{ xs: "20px", md: "28px" }}
            fontWeight={700}
            color="white"
          >
            Shipment Tracking Details
          </Typography>
        </Box>

        <Box padding={{ xs: "30px 20px", md: "40px" }}>
          <Grid container spacing={4}>
            {/* Tracking Information */}
            <Grid item xs={12}>
              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      padding: "16px",
                      background: "linear-gradient(135deg, #fff5f0 0%, #ffe8db 100%)",
                      borderRadius: "8px",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <CheckCircle sx={{ color: "#0277BD", fontSize: "20px" }} />
                      <Typography
                        fontFamily="Montserrat"
                        fontSize="15px"
                        fontWeight={700}
                        color="#1a1a2e"
                      >
                        Shipment Information
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <Box>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="10px"
                          fontWeight={600}
                          color="#777777"
                          marginBottom="2px"
                        >
                          TRACKING ID
                        </Typography>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="15px"
                          fontWeight={700}
                          color="#0277BD"
                        >
                          {shipperData.shipment.rcsTrackingId}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 0.5 }} />

                      <Box>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="10px"
                          fontWeight={600}
                          color="#777777"
                          marginBottom="2px"
                        >
                          SHIPPER NAME
                        </Typography>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="13px"
                          fontWeight={600}
                          color="#1a1a2e"
                        >
                          {shipperData.shipment.shipperName}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="10px"
                          fontWeight={600}
                          color="#777777"
                          marginBottom="2px"
                        >
                          CONSIGNEE NAME
                        </Typography>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="13px"
                          fontWeight={600}
                          color="#1a1a2e"
                        >
                          {shipperData.shipment.consigneeName}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      padding: "16px",
                      background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                      borderRadius: "8px",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <LocationOn sx={{ color: "#025D9E", fontSize: "20px" }} />
                      <Typography
                        fontFamily="Montserrat"
                        fontSize="15px"
                        fontWeight={700}
                        color="#1a1a2e"
                      >
                        Route Information
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <Box>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="10px"
                          fontWeight={600}
                          color="#777777"
                          marginBottom="4px"
                        >
                          ORIGIN
                        </Typography>
                        <Chip
                          label={shipperData.shipment.origin || "Not specified"}
                          size="small"
                          sx={{
                            background: "#025D9E",
                            color: "white",
                            fontFamily: "Montserrat",
                            fontWeight: 600,
                            fontSize: "12px",
                            height: "24px"
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "4px 0"
                        }}
                      >
                        <Box
                          sx={{
                            width: "2px",
                            height: "20px",
                            background: "linear-gradient(180deg, #025D9E 0%, #0277BD 100%)",
                            position: "relative",
                            "&::after": {
                              content: '"→"',
                              position: "absolute",
                              bottom: "-10px",
                              left: "50%",
                              transform: "translateX(-50%) rotate(90deg)",
                              color: "#0277BD",
                              fontSize: "16px",
                              fontWeight: "bold",
                            }
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          fontFamily="Montserrat"
                          fontSize="10px"
                          fontWeight={600}
                          color="#777777"
                          marginBottom="4px"
                        >
                          DESTINATION
                        </Typography>
                        <Chip
                          label={shipperData.shipment.destination || "Not specified"}
                          size="small"
                          sx={{
                            background: "#0277BD",
                            color: "white",
                            fontFamily: "Montserrat",
                            fontWeight: 600,
                            fontSize: "12px",
                            height: "24px"
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Shipment Timeline */}
            <Grid item xs={12}>
              <Box sx={{ marginTop: "20px" }}>
                <Typography
                  fontFamily="Montserrat"
                  fontSize={{ xs: "20px", md: "24px" }}
                  fontWeight={700}
                  color="#1a1a2e"
                  marginBottom="24px"
                >
                  📦 Shipment Timeline
                </Typography>

                <Box sx={{ position: "relative", padding: "20px 0" }}>
                  {shipperData.shipment.status_histories.map((row, index) => {
                    const isLast = index === shipperData.shipment.status_histories.length - 1;
                    const isFirst = index === 0;

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          position: "relative",
                          marginBottom: isLast ? 0 : "30px"
                        }}
                      >
                        {/* Connecting Line */}
                        {!isLast && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: { xs: "19px", md: "119px" },
                              top: "40px",
                              bottom: "-30px",
                              width: "2px",
                              background: isFirst ? "linear-gradient(180deg, #03A9F4 0%, #e0e0e0 100%)" : "#e0e0e0",
                              zIndex: 0
                            }}
                          />
                        )}

                        {/* Date/Time Column (Desktop: Left, Mobile: Hidden/Merged) */}
                        <Box
                          sx={{
                            width: "100px",
                            display: { xs: "none", md: "flex" },
                            flexDirection: "column",
                            textAlign: "right",
                            paddingRight: "20px",
                            paddingTop: "5px"
                          }}
                        >
                          <Typography
                            fontFamily="Montserrat"
                            fontWeight={700}
                            fontSize="14px"
                            color="#1a1a2e"
                          >
                            {new Date(row.updated_at).toLocaleDateString()}
                          </Typography>
                          <Typography
                            fontFamily="Montserrat"
                            fontWeight={500}
                            fontSize="12px"
                            color="#8d99ae"
                          >
                            {new Date(row.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>

                        {/* Dot Indicator */}
                        <Box
                          sx={{
                            width: "40px",
                            display: "flex",
                            justifyContent: "center",
                            paddingTop: "5px",
                            zIndex: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: isFirst ? "20px" : "14px",
                              height: isFirst ? "20px" : "14px",
                              borderRadius: "50%",
                              background: isFirst ? "#03A9F4" : "white",
                              border: isFirst ? "4px solid rgba(3, 169, 244, 0.2)" : "2px solid #8d99ae",
                              boxShadow: isFirst ? "0 0 0 4px rgba(3, 169, 244, 0.2)" : "none",
                              marginTop: "4px"
                            }}
                          />
                        </Box>

                        {/* Content Card */}
                        <Paper
                          elevation={0}
                          sx={{
                            flex: 1,
                            padding: "20px",
                            marginLeft: "10px",
                            borderRadius: "12px",
                            background: isFirst ? "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)" : "#f8f9fa",
                            border: "1px solid",
                            borderColor: isFirst ? "rgba(3, 169, 244, 0.2)" : "rgba(0,0,0,0.05)",
                            position: "relative",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: "-6px",
                              top: "14px",
                              width: "10px",
                              height: "10px",
                              background: isFirst ? "#f0f9ff" : "#f8f9fa",
                              transform: "rotate(45deg)",
                              borderLeft: "1px solid",
                              borderBottom: "1px solid",
                              borderColor: isFirst ? "rgba(3, 169, 244, 0.2)" : "rgba(0,0,0,0.05)",
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                            <Box>
                              <Typography
                                fontFamily="Montserrat"
                                fontWeight={700}
                                fontSize={{ xs: "16px", md: "18px" }}
                                color="#1a1a2e"
                                marginBottom="4px"
                              >
                                {row.country_city}
                              </Typography>
                              <Typography
                                fontFamily="Montserrat"
                                fontWeight={500}
                                fontSize="14px"
                                color="#4a5568"
                                sx={{ mb: 1 }}
                              >
                                {row.status}
                              </Typography>

                              {/* Mobile Date/Time */}
                              <Box sx={{ display: { xs: "flex", md: "none" }, gap: 2, mt: 1 }}>
                                <Typography fontSize="12px" color="#64748b" fontWeight={600}>
                                  📅 {new Date(row.updated_at).toLocaleDateString()}
                                </Typography>
                                <Typography fontSize="12px" color="#64748b" fontWeight={600}>
                                  ⏰ {new Date(row.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            </Box>

                            <Chip
                              label={isFirst ? "Current Status" : "Past Event"}
                              size="small"
                              variant={isFirst ? "filled" : "outlined"}
                              sx={{
                                background: isFirst ? "#03A9F4" : "transparent",
                                color: isFirst ? "white" : "#8d99ae",
                                borderColor: "#8d99ae",
                                fontFamily: "Montserrat",
                                fontWeight: 600,
                                fontSize: "11px",
                                height: "24px"
                              }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default UserSlip;